import { useEffect, useState } from "react";

import { Loader2 } from "lucide-react";

import { Progress } from "@/components/ui/progress";
import { edgeFunctionManager } from "@/lib/edgeFunctionManager";

/**
 * Global loading indicator for edge function calls
 * Shows at the top of the screen when any edge function is executing
 */
export const EdgeFunctionLoadingIndicator = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [functionName, setFunctionName] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = edgeFunctionManager.subscribe((activeCalls) => {
      const hasActiveCalls = activeCalls.size > 0;
      setIsLoading(hasActiveCalls);
      
      if (hasActiveCalls) {
        // Get the first active call to display
        const firstCall = Array.from(activeCalls.values())[0];
        setFunctionName(firstCall?.functionName || null);
      } else {
        setFunctionName(null);
      }
    });

    return unsubscribe;
  }, []);

  if (!isLoading) {
    return null;
  }

  return (
    <div className="fixed top-0 left-0 right-0 z-50">
      {/* Progress bar */}
      <div className="h-1 bg-primary/20">
        <div className="h-full bg-primary animate-pulse" style={{ width: '100%' }} />
      </div>
      
      {/* Optional: Function name indicator for debugging */}
      {import.meta.env.DEV && functionName && (
        <div className="absolute top-2 right-4 bg-background/95 backdrop-blur-xs border rounded-md px-3 py-1.5 shadow-lg">
          <div className="flex items-center gap-2 text-xs">
            <Loader2 className="h-3 w-3 animate-spin text-primary" />
            <span className="text-muted-foreground font-mono">
              {functionName}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
